import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowActionsButton,
  AdminSupportingText,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  AdminLinkAction,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  AdminFormDropdownChevron,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import defaultUserAvatar from '../../../assets/img/User.png';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  CURRENT_STAFF_MEMBER,
  FRAUD_QUEUE_ROUTE,
  SUPPORT_TEAM_MEMBERS,
  collectQueueCases,
  formatFraudDateTime,
  generateDummyCases,
  getCaseStatusFilterOptions,
  getPriorityFilterOptions,
  getSupportTeamFilterOptions,
  matchesQueueFilters,
  matchesQueueSearch,
  normalizeSupportTeam,
  upsertQueueCase,
  findQueueCase,
} from '../../../data/fraudQueueSpec';
import { getArchivedFraudCaseIdSet } from '../../../data/fraudArchiveSpec';

export { generateDummyCases };

const DEFAULT_FILTERS = {
  status: 'All',
  priority: 'All',
  supportTeam: 'All',
};

/** Case ID · Account · Source · Type · Priority · Case Reason · Support Team · Status · Received · Last Updated · Actions (old order + Case Reason / Support Team) */
const TABLE_GRID =
  'minmax(0, 1.05fr) minmax(0, 1.15fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 1.35fr) minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(62px, auto)';

const priorityTextClass = (priority) => {
  if (priority === 'High') return 'text-alertred';
  if (priority === 'Medium') return 'text-warmyellow';
  return '';
};

const FraudAlerts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusOpen, setStatusOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [supportTeamOpen, setSupportTeamOpen] = useState(false);
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const [assignPopup, setAssignPopup] = useState(null);
  const [assignMember, setAssignMember] = useState('');
  const [assignReason, setAssignReason] = useState('');

  const statusRef = useRef(null);
  const priorityRef = useRef(null);
  const supportTeamRef = useRef(null);
  const rowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: rowActionsMenuLayout,
    menuRef: rowActionsMenuRef,
    placementReady: rowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.DOUBLE
  );

  const reloadItems = () => {
    const archived = getArchivedFraudCaseIdSet();
    setItems(
      collectQueueCases().filter((row) => {
        const digits = String(row.caseId || '').replace(/\D/g, '');
        return !digits || !archived.has(digits);
      })
    );
  };

  const toggleRowActions = (rowKey) => {
    setRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(priorityRef, () => setPriorityOpen(false));
  useClickOutside(supportTeamRef, () => setSupportTeamOpen(false));

  useEffect(() => {
    reloadItems();
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch]);

  const filteredItems = useMemo(
    () =>
      items.filter(
        (row) => matchesQueueSearch(row, debouncedSearch) && matchesQueueFilters(row, filters)
      ),
    [items, filters, debouncedSearch]
  );

  const supportTeamFilterOptions = useMemo(
    () => getSupportTeamFilterOptions(items),
    [items]
  );

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(filteredItems.length / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [filteredItems.length]);

  const paginatedItems = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return filteredItems.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [filteredItems, effectivePage]);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  const openCase = (item, e) => {
    if (e) e.stopPropagation();
    navigate(`${FRAUD_QUEUE_ROUTE}/${item.caseId}`, {
      state: { from: location.pathname, item },
    });
  };

  const supportTeamLabel = (item) => {
    const name = normalizeSupportTeam(item.supportTeamMember || item.assignedTo);
    return !name || name === 'Unassigned' ? '—' : name;
  };

  const openAssignPopup = (item, mode) => {
    setRowActionsOpen({});
    setAssignPopup({ item, mode });
    setAssignMember(SUPPORT_TEAM_MEMBERS[0] || CURRENT_STAFF_MEMBER);
    setAssignReason('');
  };

  const submitAssignPopup = () => {
    if (!assignPopup?.item || !assignMember.trim() || !assignReason.trim()) return;
    const caseId = assignPopup.item.caseId;
    const current = findQueueCase(caseId) || assignPopup.item;
    const now = new Date().toISOString();
    const previous = normalizeSupportTeam(current.supportTeamMember || current.assignedTo);
    const eventLabel = assignPopup.mode === 'reassign' ? 'Reassigned' : 'Assigned';
    upsertQueueCase({
      ...current,
      status: 'In Progress',
      assignedTo: assignMember.trim(),
      supportTeamMember: assignMember.trim(),
      lastUpdated: now,
      auditLog: [
        ...(current.auditLog || []),
        {
          dateTime: now,
          event: eventLabel,
          details: `${CURRENT_STAFF_MEMBER}; ${
            previous && previous !== 'Unassigned' ? `${previous} → ` : ''
          }${assignMember.trim()}; ${assignReason.trim()}`,
          record: caseId,
        },
      ],
    });
    setAssignPopup(null);
    reloadItems();
  };

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Fraud & Security Queue</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Case ID, Account, Flag ID, Transferred From…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search Fraud & Security cases"
            />
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Case Status"
                value={filters.status}
                options={getCaseStatusFilterOptions()}
                onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Priority"
                value={filters.priority}
                options={getPriorityFilterOptions()}
                onChange={(value) => setFilters((prev) => ({ ...prev, priority: value }))}
                menuRef={priorityRef}
                open={priorityOpen}
                setOpen={setPriorityOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Support Team Member"
                value={filters.supportTeam}
                options={supportTeamFilterOptions}
                onChange={(value) => setFilters((prev) => ({ ...prev, supportTeam: value }))}
                menuRef={supportTeamRef}
                open={supportTeamOpen}
                setOpen={setSupportTeamOpen}
                size="lg"
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div className="min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                style={{ gridTemplateColumns: TABLE_GRID }}
              >
                {[
                  'Case ID',
                  'Account',
                  'Source',
                  'Type',
                  'Priority',
                  'Case Reason',
                  'Support Team Member',
                  'Case Status',
                  'Received',
                  'Last Updated',
                ].map((label) => (
                  <div key={label} className="flex items-center min-w-0">
                    <AdminTableHeader>{label}</AdminTableHeader>
                  </div>
                ))}
                <div className="flex items-center justify-center min-w-0">
                  <AdminTableHeader>Actions</AdminTableHeader>
                </div>
              </div>

              {paginatedItems.length === 0 ? (
                <div className="py-10 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    No cases match your search or filters.
                  </AdminSupportingText>
                </div>
              ) : (
                paginatedItems.map((item) => (
                  <div
                    key={item.caseId}
                    role="button"
                    tabIndex={0}
                    onClick={() => openCase(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openCase(item);
                      }
                    }}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer hover:bg-pastalBlueWhite/50"
                    style={{ gridTemplateColumns: TABLE_GRID }}
                  >
                    <div className="flex items-center min-w-0">
                      <AdminLinkAction type="button" onClick={(e) => openCase(item, e)}>
                        {item.caseId}
                      </AdminLinkAction>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <AdminRowAvatar
                        src={item.accountAvatar || defaultUserAvatar}
                        alt={item.accountName || ''}
                        onError={(e) => {
                          e.target.src = defaultUserAvatar;
                        }}
                      />
                      <AdminTableBody className="truncate" title={item.accountName}>
                        {item.accountName}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate">{item.source}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={item.caseType || '—'}>
                        {item.caseType || '—'}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center">
                      <AdminTableBody className={priorityTextClass(item.priority)}>
                        {item.priority}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={item.caseReason || '—'}>
                        {item.caseReason || '—'}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={supportTeamLabel(item)}>
                        {supportTeamLabel(item)}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminStatusBadge status={item.status} label={item.status} />
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableMeta className="whitespace-nowrap">
                        {formatFraudDateTime(item.receivedOn)}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableMeta className="whitespace-nowrap">
                        {formatFraudDateTime(item.lastUpdated)}
                      </AdminTableMeta>
                    </div>
                    <div
                      className="flex items-center justify-center relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[item.caseId] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(item.caseId);
                          }}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[item.caseId] && rowActionsMenuLayout ? (
                          <AdminFixedRowActionsMenu
                            layout={rowActionsMenuLayout}
                            menuRef={rowActionsMenuRef}
                            placementReady={rowActionsMenuPlacementReady}
                          >
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setRowActionsOpen({});
                                openCase(item, e);
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                            {item.status === 'New' ? (
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAssignPopup(item, 'assign');
                                }}
                              >
                                Assign
                              </AdminRowMenuItem>
                            ) : null}
                            {item.status === 'In Progress' ? (
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAssignPopup(item, 'reassign');
                                }}
                              >
                                Reassign
                              </AdminRowMenuItem>
                            ) : null}
                          </AdminFixedRowActionsMenu>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>

      {assignPopup && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="dashboard-panel font-inter fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
              onClick={() => setAssignPopup(null)}
              role="presentation"
            >
              <div
                className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <AdminPageTitle className="!text-lg text-center">
                  {assignPopup.mode === 'reassign' ? 'Reassign case' : 'Assign case'}
                </AdminPageTitle>
                <div className="mt-6 space-y-4">
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Team member</AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        className={adminFormDetailSelectClasses()}
                        value={assignMember}
                        onChange={(e) => setAssignMember(e.target.value)}
                      >
                        {SUPPORT_TEAM_MEMBERS.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Reason</AdminDetailFieldLabel>
                    <textarea
                      className={`${adminFormDetailTextareaClasses()} min-h-[88px]`}
                      value={assignReason}
                      onChange={(e) => setAssignReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <AdminFormSecondaryAction
                    type="button"
                    size="lg"
                    onClick={() => setAssignPopup(null)}
                  >
                    Cancel
                  </AdminFormSecondaryAction>
                  <AdminHeaderAction
                    type="button"
                    size="lg"
                    disabled={!assignMember.trim() || !assignReason.trim()}
                    onClick={submitAssignPopup}
                  >
                    Save
                  </AdminHeaderAction>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </AdminLayout>
  );
};

export default FraudAlerts;
